import type { LiveServerMessage, Session } from "@google/genai";
import {
  getAssemblyAIToken,
  getGeminiToken,
  getGradiumToken,
  getOpenAIClientSecret,
  getSonioxToken,
  openOpenAITranscriptionCall,
} from "./api";
import type { LanguageCode } from "../../shared/languages";

export interface NativeCaptionSnapshot {
  sourceSettled: string;
  sourcePreview: string;
  translationSettled: string;
  translationPreview: string;
}

export interface NativeSessionCallbacks {
  onSnapshot: (snapshot: NativeCaptionSnapshot) => void;
  onError: (message: string) => void;
}

export interface NativeTranslationSession {
  start: (stream?: MediaStream) => Promise<void>;
  stop: () => Promise<void>;
}

export type AssemblyAIMode = "fast" | "accurate" | "wide";

interface AssemblyAITurn {
  type?: string;
  transcript?: string;
  end_of_turn?: boolean;
  language_code?: string;
  error?: string;
  message?: string;
}

export interface AssemblyAITurnUpdate {
  type: "partial" | "committed";
  text: string;
  detectedLanguage?: string;
}

export const assemblyAISpeechModels: Record<AssemblyAIMode, string> = {
  fast: "universal-streaming-multilingual",
  accurate: "universal-3-5-pro",
  wide: "whisper-rt",
};

export const assemblyAIStreamingParams = (mode: AssemblyAIMode): URLSearchParams => {
  const params = new URLSearchParams({
    sample_rate: "16000",
    speech_model: assemblyAISpeechModels[mode],
  });
  if (mode === "accurate") {
    params.set("min_turn_silence", "100");
    params.set("max_turn_silence", "1000");
  } else {
    params.set("language_detection", "true");
    params.set("format_turns", "true");
    if (mode === "fast") {
      params.set("min_turn_silence", "160");
      params.set("max_turn_silence", "1000");
    }
  }
  return params;
};

export const normalizeAssemblyAITurn = (message: AssemblyAITurn): AssemblyAITurnUpdate | null => {
  if (message.type !== "Turn") return null;
  const text = message.transcript?.trim();
  if (!text) return null;
  return {
    type: message.end_of_turn ? "committed" : "partial",
    text,
    detectedLanguage: message.language_code,
  };
};

const microphoneConstraints: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: false,
  channelCount: 1,
};

const emptySnapshot = (): NativeCaptionSnapshot => ({
  sourceSettled: "",
  sourcePreview: "",
  translationSettled: "",
  translationPreview: "",
});

export const splitStreamingCaption = (text: string): { settled: string; preview: string } => {
  let boundary = -1;
  for (let index = 0; index < text.length; index += 1) {
    if (".!?。！？؟".includes(text[index])) boundary = index + 1;
  }
  return boundary < 0
    ? { settled: "", preview: text.trim() }
    : { settled: text.slice(0, boundary).trim(), preview: text.slice(boundary).trim() };
};

export const appendStreamText = (current: string, incoming: string): string => {
  if (!incoming) return current;
  if (incoming.startsWith(current)) return incoming;
  if (current.endsWith(incoming)) return current;
  return current + incoming;
};

const blobToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const block = 0x8000;
  for (let index = 0; index < bytes.length; index += block) {
    binary += String.fromCharCode(...bytes.subarray(index, index + block));
  }
  return btoa(binary);
};

const pcm16Bytes = (
  samples: Float32Array,
  inputRate: number,
  outputRate = 16_000,
): Uint8Array<ArrayBuffer> => {
  const ratio = inputRate / outputRate;
  const outputLength = Math.max(1, Math.floor(samples.length / ratio));
  const output = new Int16Array(outputLength);
  for (let index = 0; index < outputLength; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.min(samples.length, Math.max(start + 1, Math.floor((index + 1) * ratio)));
    let sum = 0;
    for (let sourceIndex = start; sourceIndex < end; sourceIndex += 1) sum += samples[sourceIndex];
    const sample = Math.max(-1, Math.min(1, sum / Math.max(1, end - start)));
    output[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return new Uint8Array(output.buffer);
};

export class AssemblyAITranscriptionSession implements NativeTranslationSession {
  private socket: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private ownsStream = false;

  constructor(
    private readonly mode: AssemblyAIMode,
    private readonly callbacks: {
      onPartial: (text: string, detectedLanguage?: string) => void;
      onCommitted: (text: string, detectedLanguage?: string) => void;
      onError: (message: string) => void;
    },
  ) {}

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const [sessionKey, stream] = await Promise.all([
      getAssemblyAIToken(),
      providedStream
        ? Promise.resolve(providedStream)
        : navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints }),
    ]);
    this.stream = stream;

    const url = new URL("wss://streaming.assemblyai.com/v3/ws");
    const params = assemblyAIStreamingParams(this.mode);
    url.searchParams.set("token", sessionKey.token);
    params.forEach((value, key) => url.searchParams.set(key, value));

    const socket = new WebSocket(url);
    this.socket = socket;
    await new Promise<void>((resolve, reject) => {
      socket.onerror = () => reject(new Error("AssemblyAI could not open its streaming transcription session."));
      socket.onopen = () => resolve();
    });

    socket.onmessage = ({ data }) => {
      const message = JSON.parse(String(data)) as AssemblyAITurn;
      if (message.error || message.type === "Error") {
        this.callbacks.onError(message.error ?? message.message ?? "AssemblyAI transcription failed.");
        return;
      }
      const update = normalizeAssemblyAITurn(message);
      if (!update) return;
      if (update.type === "committed") {
        this.callbacks.onCommitted(update.text, update.detectedLanguage);
      } else {
        this.callbacks.onPartial(update.text, update.detectedLanguage);
      }
    };
    socket.onerror = () => this.callbacks.onError("AssemblyAI lost its streaming transcription session.");

    const context = new AudioContext({ latencyHint: "interactive" });
    this.context = context;
    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(4096, 1, 1);
    const silence = context.createGain();
    silence.gain.value = 0;
    this.processor = processor;
    processor.onaudioprocess = (event) => {
      if (socket.readyState !== WebSocket.OPEN) return;
      socket.send(pcm16Bytes(event.inputBuffer.getChannelData(0), context.sampleRate));
    };
    source.connect(processor);
    processor.connect(silence);
    silence.connect(context.destination);
  }

  async stop() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "Terminate" }));
    }
    this.processor?.disconnect();
    this.processor = null;
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    this.stream = null;
    await this.context?.close();
    this.context = null;
    this.socket?.close();
    this.socket = null;
  }
}

export class OpenAITranscriptionSession implements NativeTranslationSession {
  private peer: RTCPeerConnection | null = null;
  private stream: MediaStream | null = null;
  private ownsStream = false;
  private partials = new Map<string, string>();
  private context: AudioContext | null = null;
  private turnFrame: number | null = null;
  private events: RTCDataChannel | null = null;
  private pendingSpeech = false;
  private lastVoiceAt = 0;

  constructor(
    private readonly sourceLanguage: LanguageCode,
    private readonly callbacks: {
      onPartial: (text: string) => void;
      onCommitted: (text: string) => void;
      onError: (message: string) => void;
    },
  ) {}

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const stream = providedStream
      ? providedStream
      : await navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints });
    this.stream = stream;
    const peer = new RTCPeerConnection();
    this.peer = peer;
    peer.addTrack(stream.getAudioTracks()[0], stream);
    peer.ontrack = ({ track }) => track.stop();

    const events = peer.createDataChannel("oai-events");
    this.events = events;
    events.onmessage = ({ data }) => {
      const event = JSON.parse(String(data)) as {
        type?: string;
        item_id?: string;
        delta?: string;
        transcript?: string;
        error?: { message?: string };
      };
      const itemId = event.item_id ?? "active";
      if (event.type === "conversation.item.input_audio_transcription.delta" && event.delta) {
        const text = (this.partials.get(itemId) ?? "") + event.delta;
        this.partials.set(itemId, text);
        this.callbacks.onPartial(text);
      }
      if (event.type === "conversation.item.input_audio_transcription.completed") {
        const text = event.transcript?.trim() || this.partials.get(itemId)?.trim();
        this.partials.delete(itemId);
        if (text) this.callbacks.onCommitted(text);
      }
      if (event.type === "error") {
        this.callbacks.onError(event.error?.message ?? "OpenAI live transcription failed.");
      }
    };

    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    const answerSdp = await openOpenAITranscriptionCall(offer.sdp ?? "", this.sourceLanguage);
    await peer.setRemoteDescription({ type: "answer", sdp: answerSdp });
    await this.startLocalTurnDetection(stream);
  }

  async stop() {
    if (this.turnFrame !== null) cancelAnimationFrame(this.turnFrame);
    this.turnFrame = null;
    if (this.pendingSpeech && this.events?.readyState === "open") {
      this.events.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    this.pendingSpeech = false;
    this.lastVoiceAt = 0;
    await this.context?.close();
    this.context = null;
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    this.peer?.close();
    this.peer = null;
    this.events = null;
    this.stream = null;
    this.partials.clear();
  }

  private async startLocalTurnDetection(stream: MediaStream) {
    const context = new AudioContext({ latencyHint: "interactive" });
    this.context = context;
    if (context.state === "suspended") await context.resume();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);

    const inspectAudio = () => {
      if (!this.context || !this.events) return;
      analyser.getFloatTimeDomainData(samples);
      let energy = 0;
      for (const sample of samples) energy += sample * sample;
      const rms = Math.sqrt(energy / samples.length);
      const now = performance.now();

      if (rms >= 0.012) {
        this.pendingSpeech = true;
        this.lastVoiceAt = now;
      } else if (
        this.pendingSpeech
        && this.lastVoiceAt > 0
        && now - this.lastVoiceAt >= 360
        && this.events.readyState === "open"
      ) {
        this.events.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
        this.pendingSpeech = false;
        this.lastVoiceAt = 0;
      }

      this.turnFrame = requestAnimationFrame(inspectAudio);
    };

    inspectAudio();
  }
}

export class OpenAITranslationSession implements NativeTranslationSession {
  private peer: RTCPeerConnection | null = null;
  private stream: MediaStream | null = null;
  private sourceText = "";
  private translationText = "";
  private ownsStream = false;

  constructor(
    private readonly targetLanguage: LanguageCode,
    private readonly callbacks: NativeSessionCallbacks,
  ) {}

  private publish(settleAll = false) {
    const source = settleAll ? { settled: this.sourceText.trim(), preview: "" } : splitStreamingCaption(this.sourceText);
    const translation = settleAll
      ? { settled: this.translationText.trim(), preview: "" }
      : splitStreamingCaption(this.translationText);
    this.callbacks.onSnapshot({
      sourceSettled: source.settled,
      sourcePreview: source.preview,
      translationSettled: translation.settled,
      translationPreview: translation.preview,
    });
  }

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const [secret, stream] = await Promise.all([
      getOpenAIClientSecret(this.targetLanguage),
      providedStream
        ? Promise.resolve(providedStream)
        : navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints }),
    ]);
    const { value } = secret;
    this.stream = stream;
    const peer = new RTCPeerConnection();
    this.peer = peer;
    peer.addTrack(stream.getAudioTracks()[0], stream);
    peer.ontrack = ({ track }) => track.stop();

    const events = peer.createDataChannel("oai-events");
    events.onmessage = ({ data }) => {
      const event = JSON.parse(String(data)) as { type?: string; delta?: string; error?: { message?: string } };
      if (event.type === "session.input_transcript.delta" && event.delta) {
        this.sourceText += event.delta;
        this.publish();
      }
      if (event.type === "session.output_transcript.delta" && event.delta) {
        this.translationText += event.delta;
        this.publish();
      }
      if (event.type === "error") this.callbacks.onError(event.error?.message ?? "OpenAI translation session failed.");
    };

    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    const response = await fetch("https://api.openai.com/v1/realtime/translations/calls", {
      method: "POST",
      headers: {
        authorization: `Bearer ${value}`,
        "content-type": "application/sdp",
      },
      body: offer.sdp,
    });
    if (!response.ok) throw new Error("OpenAI could not open a realtime translation call.");
    await peer.setRemoteDescription({ type: "answer", sdp: await response.text() });
  }

  async stop() {
    this.publish(true);
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    this.peer?.close();
    this.stream = null;
    this.peer = null;
  }
}

interface SonioxToken {
  text?: string;
  is_final?: boolean;
  translation_status?: "none" | "original" | "translation";
}

export const mergeSonioxTokens = (
  current: NativeCaptionSnapshot,
  tokens: SonioxToken[],
): NativeCaptionSnapshot => {
  let sourcePreview = "";
  let translationPreview = "";
  let sourceSettled = current.sourceSettled;
  let translationSettled = current.translationSettled;

  for (const tokenPart of tokens) {
    const text = tokenPart.text ?? "";
    const translated = tokenPart.translation_status === "translation";
    if (tokenPart.is_final) {
      if (translated) translationSettled += text;
      else sourceSettled += text;
    } else if (translated) translationPreview += text;
    else sourcePreview += text;
  }

  return {
    sourceSettled,
    sourcePreview,
    translationSettled,
    translationPreview,
  };
};

export class SonioxTranslationSession implements NativeTranslationSession {
  private socket: WebSocket | null = null;
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private snapshot = emptySnapshot();
  private ownsStream = false;

  constructor(
    private readonly sourceLanguage: LanguageCode,
    private readonly targetLanguage: LanguageCode,
    private readonly callbacks: NativeSessionCallbacks,
  ) {}

  private publish(settleAll = false) {
    const sourceText = this.snapshot.sourceSettled + this.snapshot.sourcePreview;
    const translationText = this.snapshot.translationSettled + this.snapshot.translationPreview;
    const source = settleAll
      ? { settled: sourceText.trim(), preview: "" }
      : splitStreamingCaption(sourceText);
    const translation = settleAll
      ? { settled: translationText.trim(), preview: "" }
      : splitStreamingCaption(translationText);
    this.callbacks.onSnapshot({
      sourceSettled: source.settled,
      sourcePreview: source.preview,
      translationSettled: translation.settled,
      translationPreview: translation.preview,
    });
  }

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const [sessionKey, stream] = await Promise.all([
      getSonioxToken(this.targetLanguage),
      providedStream
        ? Promise.resolve(providedStream)
        : navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints }),
    ]);
    const { token } = sessionKey;
    this.stream = stream;
    const socket = new WebSocket("wss://stt-rt.soniox.com/transcribe-websocket");
    this.socket = socket;

    await new Promise<void>((resolve, reject) => {
      socket.onerror = () => reject(new Error("Soniox could not open its realtime stream."));
      socket.onopen = () => {
        socket.send(JSON.stringify({
          api_key: token,
          model: "stt-rt-v5",
          audio_format: "auto",
          language_hints: this.sourceLanguage === "auto" ? undefined : [this.sourceLanguage],
          enable_language_identification: this.sourceLanguage === "auto",
          enable_endpoint_detection: true,
          max_endpoint_delay_ms: 500,
          endpoint_latency_adjustment_level: 3,
          translation: { type: "one_way", target_language: this.targetLanguage },
        }));

        const preferredType = ["audio/webm;codecs=opus", "audio/webm"]
          .find((type) => MediaRecorder.isTypeSupported(type));
        const recorder = new MediaRecorder(stream, preferredType ? { mimeType: preferredType } : undefined);
        this.recorder = recorder;
        recorder.ondataavailable = (event) => {
          if (event.data.size && socket.readyState === WebSocket.OPEN) socket.send(event.data);
        };
        recorder.start(100);
        resolve();
      };
    });

    socket.onmessage = ({ data }) => {
      const response = JSON.parse(String(data)) as {
        tokens?: SonioxToken[];
        error_message?: string;
        finished?: boolean;
      };
      if (response.error_message) {
        this.callbacks.onError(response.error_message);
        return;
      }
      this.snapshot = mergeSonioxTokens(this.snapshot, response.tokens ?? []);
      this.publish(Boolean(response.finished));
    };
    socket.onerror = () => this.callbacks.onError("Soniox lost its realtime stream.");
  }

  async stop() {
    const recorder = this.recorder;
    const socket = this.socket;
    if (recorder?.state === "recording") {
      await new Promise<void>((resolve) => {
        recorder.addEventListener("stop", () => resolve(), { once: true });
        recorder.stop();
      });
    }
    if (socket?.readyState === WebSocket.OPEN) socket.send(new ArrayBuffer(0));
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    window.setTimeout(() => socket?.close(), 250);
    this.recorder = null;
    this.stream = null;
    this.socket = null;
  }
}

export class GeminiTranslationSession implements NativeTranslationSession {
  private session: Session | null = null;
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private sourceText = "";
  private translationText = "";
  private ownsStream = false;

  constructor(
    private readonly targetLanguage: LanguageCode,
    private readonly callbacks: NativeSessionCallbacks,
  ) {}

  private publish(settleAll = false) {
    const source = settleAll ? { settled: this.sourceText.trim(), preview: "" } : splitStreamingCaption(this.sourceText);
    const translation = settleAll
      ? { settled: this.translationText.trim(), preview: "" }
      : splitStreamingCaption(this.translationText);
    this.callbacks.onSnapshot({
      sourceSettled: source.settled,
      sourcePreview: source.preview,
      translationSettled: translation.settled,
      translationPreview: translation.preview,
    });
  }

  private handleMessage(message: LiveServerMessage) {
    const content = message.serverContent;
    if (content?.inputTranscription?.text) {
      this.sourceText = appendStreamText(this.sourceText, content.inputTranscription.text);
    }
    if (content?.outputTranscription?.text) {
      this.translationText = appendStreamText(this.translationText, content.outputTranscription.text);
    }
    if (content?.inputTranscription?.text || content?.outputTranscription?.text) this.publish();
  }

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const [sessionKey, stream] = await Promise.all([
      getGeminiToken(this.targetLanguage),
      providedStream
        ? Promise.resolve(providedStream)
        : navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints }),
    ]);
    const { token } = sessionKey;
    this.stream = stream;
    const { GoogleGenAI, Modality } = await import("@google/genai");
    const ai = new GoogleGenAI({ apiKey: token, httpOptions: { apiVersion: "v1alpha" } });
    this.session = await ai.live.connect({
      model: "gemini-3.5-live-translate-preview",
      config: {
        responseModalities: [Modality.AUDIO],
        inputAudioTranscription: {},
        outputAudioTranscription: {},
        translationConfig: { targetLanguageCode: this.targetLanguage, echoTargetLanguage: true },
      },
      callbacks: {
        onmessage: (message) => this.handleMessage(message),
        onerror: () => this.callbacks.onError("Gemini lost its Live Translate stream."),
        onclose: () => undefined,
      },
    });

    const context = new AudioContext({ latencyHint: "interactive" });
    this.context = context;
    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(2048, 1, 1);
    const silence = context.createGain();
    silence.gain.value = 0;
    this.processor = processor;
    processor.onaudioprocess = (event) => {
      if (!this.session) return;
      const bytes = pcm16Bytes(event.inputBuffer.getChannelData(0), context.sampleRate);
      this.session.sendRealtimeInput({
        audio: { data: blobToBase64(bytes), mimeType: "audio/pcm;rate=16000" },
      });
    };
    source.connect(processor);
    processor.connect(silence);
    silence.connect(context.destination);
  }

  async stop() {
    this.publish(true);
    this.processor?.disconnect();
    this.processor = null;
    this.session?.close();
    this.session = null;
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    this.stream = null;
    await this.context?.close();
    this.context = null;
  }
}

const gradiumVoices: Partial<Record<LanguageCode, string>> = {
  en: "_6Aslh2DxfmnRLmP",
  fr: "jBULVCDhf05tOJN5",
  es: "m3lIeODdTQ3bOh4z",
  de: "SqFfhmAgR2XdN83R",
  pt: "AByHrwi1S-yLzW-s",
};

export class GradiumTranslationSession implements NativeTranslationSession {
  private socket: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private settledTranslation = "";
  private pendingTranslation = "";
  private settleTimer: number | null = null;
  private ownsStream = false;

  constructor(
    private readonly targetLanguage: LanguageCode,
    private readonly callbacks: NativeSessionCallbacks,
  ) {}

  private publish() {
    this.callbacks.onSnapshot({
      sourceSettled: "",
      sourcePreview: "",
      translationSettled: this.settledTranslation.trim(),
      translationPreview: this.pendingTranslation.trim(),
    });
  }

  private settlePending() {
    this.settleTimer = null;
    if (!this.pendingTranslation) return;
    this.settledTranslation += this.pendingTranslation;
    this.pendingTranslation = "";
    this.publish();
  }

  async start(providedStream?: MediaStream) {
    this.ownsStream = !providedStream;
    const voiceId = gradiumVoices[this.targetLanguage];
    if (!voiceId) throw new Error("Gradium Live Translate currently supports English, French, Spanish, German, and Portuguese targets.");
    const [sessionKey, stream] = await Promise.all([
      getGradiumToken(this.targetLanguage),
      providedStream
        ? Promise.resolve(providedStream)
        : navigator.mediaDevices.getUserMedia({ audio: microphoneConstraints }),
    ]);
    this.stream = stream;
    const url = new URL("wss://api.gradium.ai/api/speech/s2s");
    url.searchParams.set("token", sessionKey.token);
    const socket = new WebSocket(url);
    this.socket = socket;

    await new Promise<void>((resolve, reject) => {
      socket.onerror = () => reject(new Error("Gradium could not open its live translation stream."));
      socket.onopen = () => {
        socket.send(JSON.stringify({
          type: "setup",
          model_name: "s2s-translate",
          stt_model_name: "stt-translate",
          tts_model_name: "default",
          input_format: "pcm_24000",
          output_format: "pcm_48000",
          voice_id: voiceId,
          json_config: { target_language: this.targetLanguage },
        }));
      };
      socket.onmessage = ({ data }) => {
        const message = JSON.parse(String(data)) as { type?: string; text?: string; message?: string };
        if (message.type === "ready") resolve();
        if (message.type === "text" && message.text) {
          this.pendingTranslation += `${this.pendingTranslation || this.settledTranslation ? " " : ""}${message.text}`;
          this.publish();
          if (this.settleTimer === null) {
            this.settleTimer = window.setTimeout(() => this.settlePending(), 320);
          }
        }
        if (message.type === "error") this.callbacks.onError(message.message ?? "Gradium translation failed.");
      };
    });

    socket.onerror = () => this.callbacks.onError("Gradium lost its live translation stream.");
    const context = new AudioContext({ latencyHint: "interactive" });
    this.context = context;
    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(4096, 1, 1);
    const silence = context.createGain();
    silence.gain.value = 0;
    this.processor = processor;
    processor.onaudioprocess = (event) => {
      if (socket.readyState !== WebSocket.OPEN) return;
      const bytes = pcm16Bytes(event.inputBuffer.getChannelData(0), context.sampleRate, 24_000);
      socket.send(JSON.stringify({ type: "audio", audio: blobToBase64(bytes) }));
    };
    source.connect(processor);
    processor.connect(silence);
    silence.connect(context.destination);
  }

  async stop() {
    if (this.settleTimer !== null) window.clearTimeout(this.settleTimer);
    this.settleTimer = null;
    this.settlePending();
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type: "end_of_stream" }));
      window.setTimeout(() => this.socket?.close(), 250);
    }
    this.processor?.disconnect();
    this.processor = null;
    if (this.ownsStream) this.stream?.getTracks().forEach((track) => track.stop());
    this.ownsStream = false;
    this.stream = null;
    await this.context?.close();
    this.context = null;
    this.socket = null;
  }
}
